import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { MesterplanApi } from "@mesterplan/api-client";
import { RealtimeClient } from "@mesterplan/realtime-client";
import { MesterplanClientsProvider } from "@mesterplan/web-hooks";
import type { CompanyLockLike } from "@mesterplan/web-hooks";
import { useAuth } from "@mesterplan/auth-react";
import { OnboardingGate } from "@/components/onboarding/OnboardingGate";
import { LockedCompanyScreen } from "@/components/onboarding/LockedCompanyScreen";
import type { CompanyLockView, LockedCompanyOption } from "@/components/onboarding/LockedCompanyScreen";
import { persistActiveOrgId, pickActiveOrg, readStoredActiveOrgId, shouldFlagOrgNotAvailable } from "@/contexts/activeOrg.helpers";
import { isGuestPath } from "@/lib/guestPaths";
import { PlatformAdminLink } from "@/pages/admin/PlatformAdminLink";

/**
 * Exposes the active org ID derived from the user's first organisation
 * membership. Components that depend on it (e.g. useNetUser) use this to
 * re-fetch once the org is known so GET /api/users/me carries X-Active-Org.
 */
const ActiveOrgContext = createContext<string | null>(null);

/**
 * Lets the company switcher drive the active org so X-Active-Org follows the
 * user's selection. `CompanyContext` calls this from `setSelectedCompany` to
 * unify the two previously-disconnected notions of "active org" (#745).
 * Defaults to a no-op so callers on the Firebase-only path (no
 * NetClientsProvider) are safe.
 */
const SetActiveOrgContext = createContext<(orgId: string | null) => void>(() => {});

/**
 * The org the API answered `org_not_available` for (a supplier whose grant was removed), or
 * null. Set by the HTTP client's onOrgNotAvailable hook from ANY request, cleared on a switch.
 */
const OrgNotAvailableContext = createContext<string | null>(null);

/**
 * Whether the user's organisation list could not be read (so no active org can be restored) and a way to ask again.
 * Pages that wait for the active org use it to show "Prøv igen" instead of waiting for ever.
 */
export interface OrgsStatus {
  failed: boolean;
  retry: () => void;
}
const OrgsStatusContext = createContext<OrgsStatus>({ failed: false, retry: () => {} });

export function useOrgsStatus(): OrgsStatus {
  return useContext(OrgsStatusContext);
}

export function useOrgNotAvailableId(): string | null {
  return useContext(OrgNotAvailableContext);
}

export function useActiveOrgId(): string | null {
  return useContext(ActiveOrgContext);
}

export function useSetActiveOrgId(): (orgId: string | null) => void {
  return useContext(SetActiveOrgContext);
}

/**
 * Builds the .NET API and SignalR clients from the current session-cookie
 * transport and exposes them to the React tree via
 * <MesterplanClientsProvider>. Mount inside <OidcAuthProvider> (see
 * `NetAuthContext.tsx`).
 *
 * On first authenticated render it calls GET /api/users/me/organizations,
 * picks the first org, and injects X-Active-Org on every subsequent API
 * request via the getHeaders callback. This makes GET /api/users/me return
 * the user's effective permissions so the sidebar and route guards work.
 *
 * It is also the company-lock gate (M9). A locked company (Mesterplan switched
 * off from Trackrs, or not yet switched on) answers every org-scoped call with
 * 423 `company_locked`; the API client reports that through `onCompanyLocked`,
 * and `GET companies/current/lock` is read once per active org so the screen
 * appears before the first 423 rather than after it. While locked, the app is
 * replaced by `LockedCompanyScreen`.
 */
export function NetClientsProvider({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const userId = user?.id ?? null;
  const { pathname } = useLocation();
  const isPlatformAdminPath = pathname === '/admin' || pathname.startsWith('/admin/');

  const apiBaseUrl = import.meta.env.VITE_MESTERPLAN_API_URL ?? "";
  const hubBaseUrl = import.meta.env.VITE_MESTERPLAN_API_URL ?? "";

  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const [orgsFailed, setOrgsFailed] = useState(false);
  const [notAvailableOrgId, setNotAvailableOrgId] = useState<string | null>(null);
  // Bumped when a pending membership activates, so the bootstrap below reads the organisations again.
  const [orgsRefresh, setOrgsRefresh] = useState(0);
  // The bootstrap memberships, kept for the lock screen's title and switcher —
  // it renders in place of the app, so CompanyProvider is not there to ask.
  const [organizations, setOrganizations] = useState<LockedCompanyOption[]>([]);
  const [lock, setLock] = useState<CompanyLockView | null>(null);
  const queryClient = useQueryClient();

  // Ref so getHeaders always reads the latest value without recreating the
  // API client (which would break useMemo stability).
  //
  // The ref MUST be updated synchronously by whoever changes activeOrgId — not
  // in a follow-up effect. React runs child effects before parent effects, so a
  // child data-query effect keyed on activeOrgId (CompanyContext's
  // companies.get, useNetUser's /me) fires before this provider's own effects.
  // A ref synced via useEffect would still read the stale value on that first
  // request, dropping X-Active-Org and resolving permissions against an empty
  // active-org → owner gets 403 + System-only nav (#762). applyActiveOrg writes
  // the ref before the state update so getHeaders is correct by the next render.
  const activeOrgIdRef = useRef<string | null>(null);
  const applyActiveOrg = useCallback(
    (orgId: string | null) => {
      const previous = activeOrgIdRef.current;
      activeOrgIdRef.current = orgId;
      setActiveOrgId(orgId);

      // Switching company is a change of tenant, so nothing fetched for the old one
      // may survive it. Almost every query here is org-scoped only by the
      // X-Active-Org header the request carries — the response has no company on it
      // — so a cache entry keyed without the org is silently shared between
      // companies, and the next company reads the previous one's data until a
      // reload. Seen in production: a company owning no price book at all was shown
      // another company's Bygma catalogue (#2268). That was one screen; the same
      // shape reached the AI credit balance and the company's time types, which are
      // fetched with no company argument at all.
      //
      // Clearing here rather than scoping ~200 individual query keys is deliberate:
      // it is the one place every switch funnels through (the switcher calls
      // setSelectedCompany → setActiveOrgId → here), it cannot be forgotten by the
      // next query someone adds, and refetching everything is exactly what changing
      // company should do. The ref is set FIRST so anything that refetches as a
      // result already carries the new org.
      //
      // Only on an actual switch: applyActiveOrg also runs when the stored org is
      // restored on load, and clearing on every call would throw away the cache
      // continuously.
      if (previous !== null && previous !== orgId) {
        queryClient.clear();
      }
      // A lock belongs to one company; the next one is checked afresh below.
      if (previous !== orgId) {
        setLock(null);
        setNotAvailableOrgId(null);
      }
    },
    [queryClient],
  );

  const api = useMemo(
    () =>
      new MesterplanApi({
        baseUrl: apiBaseUrl,
        credentials: "include",
        getHeaders: () =>
          activeOrgIdRef.current
            ? { "X-Active-Org": activeOrgIdRef.current }
            : null,
        // Any org-scoped call answering 423 company_locked swaps the app for the
        // lock screen. setLock is a stable state setter, so the client stays memoised.
        onCompanyLocked: (locked) => setLock({ reason: locked.reason, reenableUrl: locked.reenableUrl }),
        // A 403 `org_not_available` on any call: the active org is no longer reachable (access removed).
        onOrgNotAvailable: ({ activeOrg }) => {
          if (shouldFlagOrgNotAvailable(activeOrg, activeOrgIdRef.current)) setNotAvailableOrgId(activeOrg);
        },
      }),
    [apiBaseUrl],
  );

  const realtime = useMemo(
    () =>
      new RealtimeClient({
        hubUrl: `${hubBaseUrl}/hubs/realtime`,
        withCredentials: true,
      }),
    [hubBaseUrl],
  );

  // Bootstrap: once authenticated, resolve the user's orgs so every subsequent
  // API call carries X-Active-Org and returns proper permissions. Prefer the
  // org the user last selected (if still a membership); otherwise the first.
  useEffect(() => {
    // Wait for the user id too, so the per-user stored selection can be read.
    if (isPlatformAdminPath || status !== "authenticated" || !userId || activeOrgId !== null) return;
    let cancelled = false;
    void api.me.organizations().then((res) => {
      if (cancelled) return;
      setOrgsFailed(!res.ok);
      if (res.ok) {
        // Restore the last valid choice, else first membership, else null (no
        // memberships → onboarding). persist mirrors it: stores the choice, or
        // clears a now-dangling id when the user has no memberships. Guarded on
        // res.ok so a transient error never wipes a valid selection.
        const next = pickActiveOrg(res.data, readStoredActiveOrgId(userId));
        setOrganizations(res.data.map((org) => ({ id: org.id, name: org.name })));
        if (next) applyActiveOrg(next);
        persistActiveOrgId(userId, next);
        // Only mark "resolved" on a successful response. On a transient error
        // (res.ok === false) leave orgsLoaded false so an existing user isn't
        // shown the create-first-company onboarding for a network blip — the
        // gate (orgsLoaded && activeOrgId === null) stays closed.
        setOrgsLoaded(true);
      }
    }, () => {
      if (!cancelled) setOrgsFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [status, userId, api, activeOrgId, applyActiveOrg, isPlatformAdminPath, orgsRefresh]);

  const retryOrgs = useCallback(() => {
    setOrgsFailed(false);
    setOrgsRefresh((n) => n + 1);
  }, []);
  const orgsStatus = useMemo<OrgsStatus>(() => ({ failed: orgsFailed, retry: retryOrgs }), [orgsFailed, retryOrgs]);

  // External setter (company switcher). Persists so the choice survives reload.
  const selectActiveOrg = useCallback(
    (orgId: string | null) => {
      applyActiveOrg(orgId);
      persistActiveOrgId(userId, orgId);
    },
    [userId, applyActiveOrg],
  );

  // Ask once per active org whether it is locked. The 423 path alone would get
  // there too, but only after the app has mounted and its first org-scoped call
  // has failed; this puts the answer in front of that. Not gated on: a transient
  // failure here is caught by the next 423 anyway.
  useEffect(() => {
    if (isPlatformAdminPath || status !== "authenticated" || activeOrgId === null) return;
    let cancelled = false;
    api.companies
      .currentLock()
      .then((res) => {
        if (cancelled || !res.ok || !res.data.locked) return;
        setLock({ reason: res.data.reason, reenableUrl: res.data.reenableUrl });
      })
      .catch(() => {
        // Network failure: nothing to show yet; a real lock surfaces as a 423.
      });
    return () => {
      cancelled = true;
    };
  }, [status, activeOrgId, api, isPlatformAdminPath]);

  // The awaiting_billing poll saw a change. Unlocked: drop the lock and every
  // cached read made under it, so the app remounts and fetches the company
  // fresh. Still locked, other reason: show that reason instead.
  const handleLockResolved = useCallback(
    (next: CompanyLockLike) => {
      if (next.locked) {
        setLock({ reason: next.reason, reenableUrl: next.reenableUrl });
        return;
      }
      setLock(null);
      queryClient.clear();
    },
    [queryClient],
  );

  // A guest page (a customer's quote link, a shared timeplan) belongs to whoever
  // sent the link, not to the signed-in visitor's stored company — so a lock on
  // that company must not cover it. The guest endpoints have their own lock check.
  const showLocked =
    status === "authenticated" && activeOrgId !== null && lock !== null && !isGuestPath(pathname) && !isPlatformAdminPath;
  const showOnboarding =
    status === "authenticated" && orgsLoaded && activeOrgId === null && !isPlatformAdminPath;

  function handlePendingActivated() {
    setOrgsRefresh((n) => n + 1);
  }

  function handleOrgCreated(orgId: string) {
    selectActiveOrg(orgId);
    setOrgsLoaded(true);
  }

  let content: ReactNode = children;
  if (showLocked) {
    content = (
      <LockedCompanyScreen
        activeOrgId={activeOrgId}
        lock={lock}
        companies={organizations}
        onSwitchCompany={selectActiveOrg}
        onLockResolved={handleLockResolved}
      />
    );
  } else if (showOnboarding) {
    content = <><PlatformAdminLink /><OnboardingGate onCreated={handleOrgCreated} onActivated={handlePendingActivated} /></>;
  }

  return (
    <MesterplanClientsProvider api={api} realtime={realtime}>
      <ActiveOrgContext.Provider value={activeOrgId}>
        <SetActiveOrgContext.Provider value={selectActiveOrg}>
          <OrgNotAvailableContext.Provider value={notAvailableOrgId}>
            <OrgsStatusContext.Provider value={orgsStatus}>{content}</OrgsStatusContext.Provider>
          </OrgNotAvailableContext.Provider>
        </SetActiveOrgContext.Provider>
      </ActiveOrgContext.Provider>
    </MesterplanClientsProvider>
  );
}
